import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, Stack, Typography, alpha } from '@mui/material';
import { useForm } from 'react-hook-form';
import { EditDialog } from '../../components/EditDialog/EditDialog';
import { ParamAndCoefficient, Psi } from '../../types/domainTypes';

interface ParametersAndCoefficientsDialogProps {
  data: ParamAndCoefficient;
  onClose: () => void;
}

function ValueText({ label, value }: { label: string; value?: string }) {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="body2" color={theme.palette.common.black}>
        <span style={{ color: alpha(theme.palette.common.black, 0.6) }}>
          {label}
          :&nbsp;
        </span>
        {value}
      </Typography>
    </Stack>
  );
}

function PsiSection({ psi, number }: { psi: Psi; number: string }) {
  const { i18n, t } = useTranslation();

  return (
    <Stack spacing={2}>
      <Stack direction={'row'} alignItems="center" justifyContent="space-between">
        <strong>
          &Psi;<sub>{number}</sub>
        </strong>
      </Stack>

      <ValueText
        label={t('project-details-page.parameters-and-coefficients-dialog.label-snow')}
        value={psi.snow.toLocaleString(i18n.language)}
      />
      {psi.snowCoefficient && (
        <ValueText
          label={t('project-details-page.parameters-and-coefficients-dialog.label-snow-coefficient')}
          value={psi.snowCoefficient.toLocaleString(i18n.language)}
        />
      )}
      <ValueText
        label={t('project-details-page.parameters-and-coefficients-dialog.label-wind')}
        value={psi.wind.toLocaleString(i18n.language)}
      />
      {psi.windCoefficient && (
        <ValueText
          label={t('project-details-page.parameters-and-coefficients-dialog.label-wind-coefficient')}
          value={psi.windCoefficient.toLocaleString(i18n.language)}
        />
      )}
      <ValueText
        label={t('project-details-page.parameters-and-coefficients-dialog.label-temperature')}
        value={psi.temperature.toLocaleString(i18n.language)}
      />
      {psi.temperatureCoefficient && (
        <ValueText
          label={t('project-details-page.parameters-and-coefficients-dialog.label-temperature-coefficient')}
          value={psi.temperatureCoefficient.toLocaleString(i18n.language)}
        />
      )}
      <ValueText label={t('project-details-page.parameters-and-coefficients-dialog.label-live')} value={psi.live ? '1' : '0'} />
    </Stack>
  );
}

export function ParametersAndCoefficientsDialog({ data, onClose }: ParametersAndCoefficientsDialogProps): ReactElement {
  const { t } = useTranslation();

  const { handleSubmit } = useForm<ParamAndCoefficient>();

  return (
    <EditDialog
      onClose={onClose}
      handleFormSubmit={handleSubmit(onClose)}
      isDirty={false}
      title={t('project-details-page.parameters-and-coefficients-dialog.view-parameters-and-coefficients')}
      saveActionText={t('project-details-page.parameters-and-coefficients-dialog.action-dismiss')}
    >
      <Stack spacing={4}>
        <PsiSection psi={data.psi0} number={'0'} />
        <PsiSection psi={data.psi1} number={'1'} />
      </Stack>
    </EditDialog>
  );
}
